import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, deepStringFind, Enums, inject, TableHeadOptions, SortOptions } from 'Mri.Web.Common';
import * as React from 'react';
import { DropdownMenu, DropdownToggle, Input, UncontrolledButtonDropdown } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { DisplayListViewModel } from './DisplayListViewModel';
import { IDisplayCellMeta } from '../Displays/DisplayCells';
import { displayTelemetryCells } from '../Displays/DisplayCells_Telemetry';
import { displayAggregateCells } from '../Displays/DisplayCells_Aggregate';
import { BsArrowDownShort } from '@react-icons/all-files/bs/BsArrowDownShort';

export interface ISortSelectorProps<T> extends React.Props<SortSelector<T>> {
    readonly vm: DisplayListViewModel;
    readonly className?: string;
    readonly aggregateView?: boolean;
    readonly link?: boolean;
}

@autoinject
@observer
export class SortSelector<T> extends React.Component<ISortSelectorProps<T>> {
    public render() {
        const { onSortChange } = this.props.vm;

        return (
            <UncontrolledButtonDropdown
                isOpen={this.dropdownOpen}
                toggle={this.onToggle}
                className={this.props.className}
            >
                <DropdownToggle caret size="sm" color="secondary">
                    <BsArrowDownShort />
                </DropdownToggle>
                <DropdownMenu end className="p-0 dropdown-menu-left added-dropdown-width dropdown-height">
                    <Input
                        type="text"
                        placeholder="Find Sort Options..."
                        value={this.findString}
                        onChange={this.onFindChange}
                        autoFocus
                    />
                    <button className="text-center dropdown-item" onClick={() => onSortChange([])}>
                        Reset
                    </button>

                    {this.options?.filter(o => o != null).map(this.renderEntry)}

                    {this.props.children}
                </DropdownMenu>
            </UncontrolledButtonDropdown>
        );
    }

    @bind
    private renderEntry(entry: IDisplayCellMeta, key: any) {
        const { sort, onSortChange } = this.props.vm;
        const { id, field } = entry;
        const entryName =
            typeof id == 'number' && Enums.MeasurementId.has(id) ? Enums.toString(Enums.MeasurementId, id) : id;
        const sortField = field != null ? field : typeof id == 'number' ? id.toString() : null;
        const sortStatus = sortField ? sort.find(s => s.startsWith(sortField)) : null;
        let style = 'px-1 ps-0 pe-4 sort-dropdown-item';

        if (sortStatus) {
            const order = sortStatus.split(' ')[1];

            if (order == SortOptions.Ascending || order == SortOptions.Descending) {
                style += ' active';
            }
        }

        return (
            <div key={key} className={style}>
                {sortField && (
                    <TableHeadOptions
                        value={sort}
                        field={sortField}
                        onChange={onSortChange}
                        exclusive={this.accountStore.user?.multiSortPreferred != true}
                        className="m-1 form-check-label w-100 d-flex"
                        label={entryName as string}
                        button
                    >
                        {entryName}
                    </TableHeadOptions>
                )}
            </div>
        );
    }

    @computed
    private get options() {
        if (!this.columnOptions) return;

        let items = this.columnOptions.slice();

        if (this.findString) items = items.filter(e => deepStringFind(this.print(e.id), this.findString));

        return items;
    }

    @bind
    @action
    private onToggle() {
        this.dropdownOpen = !this.dropdownOpen;
        this.findString = '';
    }

    @bind
    @action
    private onFindChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.findString = target.value;
    }

    @computed
    private get columnOptions() {
        return this.props.aggregateView ? displayAggregateCells : displayTelemetryCells;
    }

    private print(value: string | Mri.WebApi.MeasurementId) {
        return typeof value == 'number' ? Enums.toString(Enums.MeasurementId, value) : value;
    }

    @inject
    private accountStore!: AccountStore;
    @observable
    private findString: string = '';
    @observable
    private dropdownOpen = false;
}
